import { InformationCircleIcon } from '@heroicons/react/24/solid';
import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	Alert,
	Chip,
	Input,
	Option,
	Select,
	ThemeProvider,
	Tooltip,
	Typography,
} from '@material-tailwind/react';
import _ from 'lodash';
import { useState } from 'react';
import { Invoice, InvoiceSearchParams, ProducerLocation } from '../../types';
import { formatDateLong, formatFirstLetterUpperCase } from '../lib/formatters';
import { formatMoney } from '../lib/money';
import DownIcon from './Icons/DownIcon';
import InvoiceReceipt from './InvoiceReceipt';
import { Pagination } from './Pagination';

const theme = {
	input: {
		styles: {
			base: {
				container: {
					minWidth: 'min-w-[50px]',
				},
			},
		},
	},
};

interface PaginationProps {
	activePage: number;
	setActivePage: (activePage: number) => void;
}

interface Props {
	invoices: Invoice[];
	invoicesLoaded: boolean;
	setInvoiceSearchParams: (value: InvoiceSearchParams) => void;
	invoiceSearchParams: InvoiceSearchParams | null;
	paginationProps: PaginationProps;
    producerLocations?: ProducerLocation[] | null;
}

const InvoiceTable = ({ invoices, invoicesLoaded, invoiceSearchParams, setInvoiceSearchParams, paginationProps, producerLocations }: Props) => {
	// const { date, pageIndex, transactionStatus, sortDirection } = invoiceSearchParams || {};

	const invoicesHavePLKs = invoices.some((invoice) => invoice.producerLocationKey);
	const producerLocationDropdownOptionsExist = producerLocations && !_.isEmpty(producerLocations);
	const locationColumnExists = invoicesHavePLKs || producerLocationDropdownOptionsExist;

	const headers = [
		{
			label: 'Invoice Date',
			fieldName: 'startDate',
			rowValueName: 'startDate',
			inputType: 'startDate',
		},
		locationColumnExists
			? {
					label: 'Location',
					fieldName: 'producerLocationKey',
					rowValueName: 'producerLocationKey',
					setter: producerLocationDropdownOptionsExist
						? (producerLocationKey) =>
								setInvoiceSearchParams({ ...invoiceSearchParams, producerLocationKey })
						: null,
					customComponent: producerLocationDropdownOptionsExist
						? (setter) => plkSelector({ producerLocations, setter })
						: null,
			  }
			: null,
		{
			label: 'Net Amount Owed',
			fieldName: 'net',
			rowValueName: 'net',
		},
		{
			label: 'Charge Status',
			fieldName: 'invoiceTransactionStatus',
			rowValueName: 'invoiceTransactionStatus',
		},
	].filter(Boolean);

	return (
		<ThemeProvider value={theme}>
			<div className="flex justify-between items-center p-3 h-20">
				<h5 className="text-xl font-semibold">Invoices</h5>
			</div>
			<div className="flex-col max-h-[calc(100vh-10rem)] items-center">
				<table className="border mt-4 w-full text-left">
					<TableHeaders
						headers={headers}
					/>
					<TableRows
						rows={invoices}
						invoicesLoaded={invoicesLoaded}
						headers={headers}
						locationColumnExists={locationColumnExists}
					/>
				</table>
				{paginationProps && (paginationProps.activePage !== 1 || invoices.length >= 10) && (
					<Pagination activePage={paginationProps.activePage} setActivePage={paginationProps.setActivePage} />
				)}
			</div>
		</ThemeProvider>
	);
};

const TableHeaders = ({ headers }) => {
	const [headerValues, setHeaderValues] = useState(headers.map((h) => h.initialValue));

	return (
		<thead>
			<tr>
				{headers.map((head, index) => (
					<th
						key={head.label}
						className={`cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50 ${
							head.width || ''
						}`}
						colSpan={head.colSpan || 1}
					>
						<div className="flex items-center justify-between gap-2 font-normal leading-none">
							{!head.customComponent ? (
								<div className="w-full gap-6 opacity-70">
									{head.setter ? (
										<Input
											label={head.label}
											crossOrigin=""
											variant="standard"
											size="md"
											value={headerValues[index]}
											placeholder={head.placeholder}
											type={head.inputType}
											onChange={(event) => {
												setHeaderValues({
													...headerValues,
													[index]: event.target.value,
												});
												head.setter(event.target.value);
											}}
										/>
									) : (
										<Typography variant="small" color="blue-gray" className="font-[420]">
											{head.label}
										</Typography>
									)}
								</div>
							) : (
								head.customComponent(head.setter)
							)}
						</div>
					</th>
				))}
			</tr>
		</thead>
	);
};

const TableRows = ({ rows, invoicesLoaded, headers, locationColumnExists }) => {
	const [open, setOpen] = useState(0);
	const handleOpen = (value) => setOpen(open === value ? 0 : value);

	return (
		<tbody>
			{!invoicesLoaded && rows.length === 0 && (
				<tr className="border-b-0">
					<td colSpan={headers.map((h) => h.colSpan || 1).reduce((a, b) => a + b)} className="p-8 py-4">
						<Alert color="orange" variant="ghost">
							Loading...
						</Alert>
					</td>
				</tr>
			)}
			{rows.map((invoice, index) => {
				const {
					url,
					invoiceId,
					invoiceTransactionStatus,
					startTime,
					endTime,
					net,
					cash,
					refund,
					producerLocationPay,
					tipFee,
					relayFee,
					recurringCharges,
					nachaGroupItems,
                    producerLocationName,
				} = invoice;

				const isLast = index === rows.length - 1;
				const classes = isLast ? 'p-4' : 'p-4 border-b border-blue-gray-50';


				return (
					<tr
						key={invoiceId + `${index}`}
						className={
							invoicesLoaded ? 'cursor-pointer hover:bg-gray-100' : 'pointer-events-none bg-gray-100'
						}
						onClick={() => window.open(url)}
					>
						<td className={classes}>
							<Typography variant="small" color="blue-gray" className="font-normal">
								{`${formatDateLong(startTime)} - ${formatDateLong(endTime)}`}
							</Typography>
						</td>
						{(locationColumnExists) ? (
							<td className={classes}>
								<Typography variant="small" color="blue-gray" className="font-normal">
									{producerLocationName || ''}
								</Typography>
							</td>
						) : null}
						<td className={classes}>
							<div
								className="flex flex-col"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleOpen(index + 1);
								}}
							>
								<Accordion open={open === index + 1}>
									<AccordionHeader className="flex justify-start gap-2 border-b-0">
										<DownIcon open={open === index + 1}/>
										<Typography variant="small" color="blue-gray" className="font-semibold">
											{formatMoney(net)}
										</Typography>
									</AccordionHeader>
									<AccordionBody>
										<InvoiceReceipt
											net={net}
											producerLocationPay={producerLocationPay}
											cash={cash}
											tipFee={tipFee}
											refund={refund}
											relayFee={relayFee}
											recurringCharges={recurringCharges}
										/>
									</AccordionBody>
								</Accordion>
							</div>
						</td>
						<td className={classes}>
							<div className="flex w-full">
								<InvoiceChargeChip nachaGroupItems={nachaGroupItems} />
							</div>
						</td>
					</tr>
				);
			})}
		</tbody>
	);
};

const InvoiceChargeChip = ({ nachaGroupItems }: any) => {

    const latestNachaGroupItem = _.maxBy(nachaGroupItems, (nachaGroupItem) => nachaGroupItem.createdAt);

	return latestNachaGroupItem ? (
        <div className="flex flex-col">
            <Tooltip
                content={
                    latestNachaGroupItem.wasReturned && <div className="italic flex">
                        {formatFirstLetterUpperCase(latestNachaGroupItem.returnDescription)}
                    </div>
                }
                placement="left"
                className={`${latestNachaGroupItem.wasReturned ? `` : `hidden`}`}
            >
            <Chip
                size="lg"
                value={latestNachaGroupItem.wasReturned ? 'Charge Failed' : `Charged on ${formatDateLong(latestNachaGroupItem.createdAt)}`}
                color={latestNachaGroupItem.wasReturned ? 'red' :'green'}
                icon={
                	latestNachaGroupItem.wasReturned ? (
                		<InformationCircleIcon className={`h-6 w-6 text-white`} />
                	) : null
                }
            />
            </Tooltip>
        </div>
	) : (
		<Chip
            size="lg"
            value={`Pending`}
            variant={`ghost`}
            color={'orange'}
        />
	);
};

const plkSelector = ({producerLocations, setter}) => {
	return (
		<div className="gap-6 z-10">
			<Select label="Location" onChange={setter}>
                {producerLocations.map(({producerLocationKey, producerLocationName}) => 
                    <Option value={producerLocationKey}>
                        {producerLocationName}
				    </Option>
                )}
			</Select>
		</div>
	);
};

export default InvoiceTable;
